import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, CreditCard } from 'lucide-react';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'ENGAGEMENT CONTEXT', sublabel: 'Contact information' },
  { id: 2, label: 'ENVIRONMENT SCOPE', sublabel: 'System scale' },
  { id: 3, label: 'OBSERVED SIGNALS', sublabel: 'Operational indicators' },
  { id: 4, label: 'DATA READINESS', sublabel: 'Export capability' },
  { id: 5, label: 'SUBMISSION PARAMETERS', sublabel: 'Timeline and notes' },
  { id: 6, label: 'CALL AVAILABILITY', sublabel: 'Pre-Diagnostics scheduling' },
];

const SIGNAL_OPTIONS = [
  'Service level shortfall at specific intervals despite adequate headcount',
  'Overstaffing in some intervals concurrent with SL misses in others',
  'Forecast accuracy declining over time',
  'Adherence compliance not correlating with SL outcomes',
  'Recurring shortfall on specific queue types only',
  'Intraday management unable to course-correct',
  'Occupancy-SL inversion (high occupancy, SL failure)',
  'Scheduled headcount not matching staffed headcount patterns',
];

const WFM_PLATFORMS = ['Aspect WFM', 'NICE IEX WFM', 'Verint WFM', 'Genesys WFM', 'Calabrio WFM', 'UKG / Workforce Now', 'Injixo', 'Alvaria', 'Other'];

const TIMEZONES = [
  'America/New_York (Eastern)',
  'America/Chicago (Central)',
  'America/Denver (Mountain)',
  'America/Los_Angeles (Pacific)',
  'America/Anchorage (Alaska)',
  'Pacific/Honolulu (Hawaii)',
  'Europe/London (GMT/BST)',
  'Europe/Paris (CET)',
  'Europe/Berlin (CET)',
  'Asia/Dubai (GST)',
  'Asia/Singapore (SGT)',
  'Asia/Tokyo (JST)',
  'Australia/Sydney (AEDT)',
  'Other (specify in slot)',
];

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-mono tracking-widest uppercase text-zinc-500 mb-2">
    {children}{required && <span className="text-amber-500 ml-1">*</span>}
  </label>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', testid }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200"
  />
);

const SelectInput = ({ value, onChange, options, placeholder, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 outline-none transition-colors duration-200 appearance-none cursor-pointer"
  >
    <option value="" className="text-zinc-600">{placeholder}</option>
    {options.map((opt) => (
      <option key={opt} value={opt} className="text-zinc-200 bg-zinc-900">{opt}</option>
    ))}
  </select>
);

const RadioOption = ({ label, value, selected, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(value)}
    className={`text-left px-4 py-3 rounded-md border text-sm transition-colors duration-200 ${
      selected
        ? 'border-amber-500/50 bg-amber-500/5 text-zinc-100'
        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
    }`}
  >
    {label}
  </button>
);

const CheckOption = ({ label, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-start gap-3 text-left px-4 py-3 rounded-md border text-sm transition-colors duration-200 w-full ${
      checked
        ? 'border-amber-500/50 bg-amber-500/5 text-zinc-200'
        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
    }`}
  >
    <div className={`mt-0.5 w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border ${checked ? 'bg-amber-500 border-amber-500' : 'border-zinc-600'}`}>
      {checked && <Check size={10} className="text-zinc-900" />}
    </div>
    <span className="leading-relaxed">{label}</span>
  </button>
);

const initialData = {
  // Step 1: Engagement Context
  full_name: '', title: '', organization: '', email: '', phone: '',
  // Step 2: Environment Scope
  queue_count: '', headcount_range: '', wfm_platform: '', multi_site: false,
  // Step 3: Observed Signals
  observed_signals: [], signal_description: '',
  // Step 4: Data Readiness
  has_interval_data: '', export_format: '', interval_granularity: '', history_available: '',
  // Step 5: Submission Parameters
  preferred_timeline: '', referral_source: '', notes: '',
  // Step 6: Scheduling (NEW)
  timezone: '', slot1: '', slot2: '', slot3: '',
};

export function Intake() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [legalFlags, setLegalFlags] = useState({
    acceptTermsOfService: false,
    acceptDataProcessing: false,
    acknowledgeNonRefundable: false,
  });

  const update = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

  const toggleSignal = (signal) => {
    setData((prev) => ({
      ...prev,
      observed_signals: prev.observed_signals.includes(signal)
        ? prev.observed_signals.filter((s) => s !== signal)
        : [...prev.observed_signals, signal],
    }));
  };

  const canAdvance = () => {
    if (step === 1) return data.full_name && data.title && data.organization && data.email;
    if (step === 2) return data.queue_count && data.headcount_range && data.wfm_platform;
    if (step === 3) return data.observed_signals.length > 0;
    if (step === 4) return data.has_interval_data;
    if (step === 5) return data.preferred_timeline;
    if (step === 6) {
      const hasRequiredFields = data.timezone && data.slot1;
      const hasAllLegalChecks = legalFlags.acceptTermsOfService && 
                                legalFlags.acceptDataProcessing && 
                                legalFlags.acknowledgeNonRefundable;
      return hasRequiredFields && hasAllLegalChecks;
    }
    return false;
  };

  // Build the full intake payload
  const buildIntakePayload = () => {
    return {
      engagementContext: {
        full_name: data.full_name,
        title: data.title,
        organization: data.organization,
        email: data.email,
        phone: data.phone || null,
      },
      environmentScope: {
        queue_count: data.queue_count,
        headcount_range: data.headcount_range,
        wfm_platform: data.wfm_platform,
        multi_site: data.multi_site,
      },
      observedSignals: {
        observed_signals: data.observed_signals,
        signal_description: data.signal_description || null,
      },
      dataReadiness: {
        has_interval_data: data.has_interval_data,
        export_format: data.export_format || null,
        interval_granularity: data.interval_granularity || null,
        history_available: data.history_available || null,
      },
      submissionParams: {
        preferred_timeline: data.preferred_timeline,
        referral_source: data.referral_source || null,
        notes: data.notes || null,
      },
      scheduling: {
        timezone: data.timezone,
        slot1: data.slot1,
        slot2: data.slot2 || null,
        slot3: data.slot3 || null,
      },
      fullName: data.full_name,
      organization: data.organization,
      businessEmail: data.email,
      phone: data.phone || null,
    };
  };

  const handleSubmitAndPay = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      const intakePayload = buildIntakePayload();
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/create-checkout-session`, {
        intake: intakePayload,
        origin_url: originUrl,
      });
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (e) {
      console.error('Checkout error:', e);
      const errorMessage = e.response?.data?.detail || e.message || 'Failed to initiate payment. Please try again or contact us directly.';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-950 min-h-screen">
      <SEOHead />
      <div className="relative section-border py-16 overflow-hidden">
        <div className="absolute inset-0 grid-texture pointer-events-none" />
        <div className="container-site relative z-10 max-w-3xl">
          <div className="mb-10">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-4">
              Engagement Initiation
            </span>
            <h1 className="font-heading text-4xl font-medium tracking-tight text-zinc-100">
              Intake Protocol
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              Complete all steps to initiate a Onpoint Diagnostics engagement. Typical completion: 8–12 minutes.
            </p>
          </div>

          {/* Progress */}
          <div className="mb-10">
            <div className="flex items-center gap-0 mb-4 overflow-x-auto">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex items-center">
                  <div
                    data-testid={`intake-step-indicator-${s.id}`}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono border transition-colors duration-200 ${
                      step > s.id
                        ? 'bg-amber-500 border-amber-500 text-zinc-900'
                        : step === s.id
                        ? 'bg-zinc-100 border-zinc-100 text-zinc-900'
                        : 'bg-transparent border-zinc-700 text-zinc-600'
                    }`}
                  >
                    {step > s.id ? <Check size={12} /> : String(s.id).padStart(2, '0')}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`w-8 md:w-12 h-px flex-shrink-0 ${step > s.id ? 'bg-amber-500/40' : 'bg-zinc-800'}`} />
                  )}
                </div>
              ))}
            </div>
            <div>
              <h2 className="text-xs font-mono tracking-widest uppercase text-amber-500">
                STEP {String(step).padStart(2, '0')} — {STEPS[step - 1].label}
              </h2>
              <span className="text-xs text-zinc-600 ml-3">{STEPS[step - 1].sublabel}</span>
            </div>
          </div>

          {/* Step content */}
          <div className="border border-zinc-800 rounded-md p-8 bg-zinc-900/20">
            {step === 1 && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Full Name</FieldLabel>
                    <TextInput value={data.full_name} onChange={(v) => update('full_name', v)} placeholder="Tekkel Schenkel" testid="intake-full-name" />
                  </div>
                  <div>
                    <FieldLabel required>Title / Role</FieldLabel>
                    <TextInput value={data.title} onChange={(v) => update('title', v)} placeholder="VP of Operations" testid="intake-title" />
                  </div>
                </div>
                <div>
                  <FieldLabel required>Organization</FieldLabel>
                  <TextInput value={data.organization} onChange={(v) => update('organization', v)} placeholder="Acme Corporation" testid="intake-organization" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Business Email</FieldLabel>
                    <TextInput value={data.email} onChange={(v) => update('email', v)} placeholder="jane@acme.com" type="email" testid="intake-email" />
                  </div>
                  <div>
                    <FieldLabel>Phone (optional)</FieldLabel>
                    <TextInput value={data.phone} onChange={(v) => update('phone', v)} placeholder="+1 (555) 000-0000" testid="intake-phone" />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <FieldLabel required>Number of queues / skill groups</FieldLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['1–5', '6–15', '16–50', '50+'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.queue_count === v} onChange={(val) => update('queue_count', val)} testid={`intake-queue-${v}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel required>Agent headcount range</FieldLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {['< 50', '50–200', '200–500', '500–1,500', '1,500+'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.headcount_range === v} onChange={(val) => update('headcount_range', val)} testid={`intake-headcount-${v.replace(/[^a-z0-9]/gi, '-')}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel required>WFM platform in use</FieldLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {WFM_PLATFORMS.map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.wfm_platform === v} onChange={(val) => update('wfm_platform', val)} testid={`intake-platform-${v.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Multi-site environment?</FieldLabel>
                  <div className="flex gap-3">
                    {['Yes', 'No'].map((v) => (
                      <RadioOption key={v} label={v} value={v === 'Yes'} selected={data.multi_site === (v === 'Yes')} onChange={(val) => update('multi_site', val)} testid={`intake-multisite-${v.toLowerCase()}`} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <FieldLabel required>Observed signals (select all that apply)</FieldLabel>
                  <div className="space-y-2">
                    {SIGNAL_OPTIONS.map((signal) => (
                      <CheckOption
                        key={signal}
                        label={signal}
                        checked={data.observed_signals.includes(signal)}
                        onChange={() => toggleSignal(signal)}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Describe what you are observing (optional)</FieldLabel>
                  <textarea
                    value={data.signal_description}
                    onChange={(e) => update('signal_description', e.target.value)}
                    placeholder="Provide additional context on the operational patterns you have observed..."
                    data-testid="intake-signal-description"
                    rows={4}
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200 resize-none"
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <FieldLabel required>Can you export interval-level WFM data?</FieldLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {['Yes', 'Not sure', 'Need to check'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.has_interval_data === v} onChange={(val) => update('has_interval_data', val)} testid={`intake-data-readiness-${v.replace(/\s+/g, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Available export format</FieldLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {['CSV', 'XLSX', 'Both'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.export_format === v} onChange={(val) => update('export_format', val)} testid={`intake-export-format-${v.toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Interval granularity available</FieldLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['15-minute', '30-minute', '60-minute', 'Not sure'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.interval_granularity === v} onChange={(val) => update('interval_granularity', val)} testid={`intake-granularity-${v.replace(/\s+/g, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>History available for export</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {['1–3 months', '3–6 months', '6–12 months', '12+ months'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.history_available === v} onChange={(val) => update('history_available', val)} testid={`intake-history-${v.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <FieldLabel required>Preferred engagement timeline</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {['Within 2 weeks', '1–3 months', 'Next quarter', 'Evaluating options'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.preferred_timeline === v} onChange={(val) => update('preferred_timeline', val)} testid={`intake-timeline-${v.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>How did you hear about OnPoint.Works?</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {['Referral / Colleague', 'LinkedIn', 'Web search', 'Industry event', 'Other'].map((v) => (
                      <RadioOption key={v} label={v} value={v} selected={data.referral_source === v} onChange={(val) => update('referral_source', val)} testid={`intake-referral-${v.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Additional notes (optional)</FieldLabel>
                  <textarea
                    value={data.notes}
                    onChange={(e) => update('notes', e.target.value)}
                    placeholder="Any additional context, questions, or constraints relevant to this engagement..."
                    data-testid="intake-notes"
                    rows={4}
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200 resize-none"
                  />
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-6">
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-4 mb-6">
                  <h3 className="text-sm font-medium text-amber-400 mb-2">Pre-Diagnostics Call Availability</h3>
                  <p className="text-xs text-zinc-400">
                    Please provide your timezone and at least one preferred time slot for the Pre-Diagnostics Call. 
                    We will confirm one of your proposed slots by email within 1–2 business days.
                  </p>
                </div>
                
                <div>
                  <FieldLabel required>Your Timezone</FieldLabel>
                  <SelectInput 
                    value={data.timezone} 
                    onChange={(v) => update('timezone', v)} 
                    options={TIMEZONES}
                    placeholder="Select your timezone"
                    testid="intake-timezone"
                  />
                </div>
                
                <div>
                  <FieldLabel required>Preferred Slot #1</FieldLabel>
                  <TextInput 
                    value={data.slot1} 
                    onChange={(v) => update('slot1', v)} 
                    placeholder="e.g., Tuesday, March 4th, 2:00 PM EST" 
                    testid="intake-slot1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Date, time, and timezone (if different from above)</p>
                </div>
                
                <div>
                  <FieldLabel>Preferred Slot #2 (optional)</FieldLabel>
                  <TextInput 
                    value={data.slot2} 
                    onChange={(v) => update('slot2', v)} 
                    placeholder="e.g., Wednesday, March 5th, 10:00 AM EST" 
                    testid="intake-slot2"
                  />
                </div>
                
                <div>
                  <FieldLabel>Preferred Slot #3 (optional)</FieldLabel>
                  <TextInput 
                    value={data.slot3} 
                    onChange={(v) => update('slot3', v)} 
                    placeholder="e.g., Thursday, March 6th, 3:00 PM EST" 
                    testid="intake-slot3"
                  />
                </div>

                {/* Legal Acknowledgments */}
                <div className="border border-zinc-700 rounded-md p-5 mt-6 bg-zinc-900/30">
                  <h3 className="text-sm font-medium text-zinc-200 mb-1">Legal Acknowledgments</h3>
                  <p className="text-xs text-zinc-500 mb-4">Please review and accept the following before proceeding to payment:</p>
                  
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="legal-tos-label">
                      <input
                        type="checkbox"
                        data-testid="legal-tos"
                        checked={legalFlags.acceptTermsOfService}
                        onChange={(e) => setLegalFlags(prev => ({ ...prev, acceptTermsOfService: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500/30 cursor-pointer"
                      />
                      <span className="text-sm text-zinc-300 group-hover:text-zinc-100 transition-colors">
                        I accept the <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">Terms of Service</a>
                      </span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="legal-data-label">
                      <input
                        type="checkbox"
                        data-testid="legal-data"
                        checked={legalFlags.acceptDataProcessing}
                        onChange={(e) => setLegalFlags(prev => ({ ...prev, acceptDataProcessing: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500/30 cursor-pointer"
                      />
                      <span className="text-sm text-zinc-300 group-hover:text-zinc-100 transition-colors">
                        I accept the Data Processing & Confidentiality Protocol
                      </span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="legal-refund-label">
                      <input
                        type="checkbox"
                        data-testid="legal-refund"
                        checked={legalFlags.acknowledgeNonRefundable}
                        onChange={(e) => setLegalFlags(prev => ({ ...prev, acknowledgeNonRefundable: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500/30 cursor-pointer"
                      />
                      <span className="text-sm text-zinc-300 group-hover:text-zinc-100 transition-colors">
                        I acknowledge that the Pre-Diagnostics Call fee is non-refundable
                      </span>
                    </label>
                  </div>
                </div>

                {error && (
                  <p data-testid="intake-error" className="text-sm text-memo-warn bg-memo-warn-bg border border-memo-warn px-4 py-3 rounded-md">
                    {error}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              data-testid="intake-back-btn"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-800 text-zinc-400 rounded-md text-sm hover:border-zinc-600 hover:text-zinc-200 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={15} /> Previous
            </button>
            <span className="text-xs font-mono text-zinc-600">
              {step} / {STEPS.length}
            </span>
            {step < 6 ? (
              <button
                data-testid="intake-next-btn"
                onClick={() => setStep((s) => Math.min(6, s + 1))}
                disabled={!canAdvance()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ChevronRight size={15} />
              </button>
            ) : (
              <button
                data-testid="intake-submit-btn"
                onClick={handleSubmitAndPay}
                disabled={!canAdvance() || submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-zinc-900 rounded-md text-sm font-medium hover:bg-amber-400 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    Submit Intake & Continue to Payment
                    <CreditCard size={15} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
